import * as i0 from '@angular/core';
import { Inject, Injectable, Input, Directive, InjectionToken, NgModule } from '@angular/core';
import { Log, SDKType } from '@statsig/client-core';
import { BehaviorSubject } from 'rxjs';
import { StatsigClient } from '@statsig/js-client';

function Memoize(keyGenerator) {
    return function (_target, _propertyKey, descriptor) {
        const orignal = descriptor.value;
        const cache = new Map();
        descriptor.value = function (...args) {
            const key = keyGenerator(...args);
            if (cache.has(key)) {
                return cache.get(key);
            }
            const result = orignal.apply(this, args);
            cache.set(key, result);
            return result;
        };
        return descriptor;
    };
}

var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
class StatsigService {
    _renderVersion = 0;
    _client;
    _isLoadingSubject = new BehaviorSubject(true);
    isLoading$ = this._isLoadingSubject.asObservable();
    constructor(provider) {
        if ('client' in provider) {
            this._client = provider.client;
        }
        else if ('sdkKey' in provider && 'user' in provider) {
            this._client = new StatsigClient(provider.sdkKey, provider.user, provider.options);
            this._client
                .initializeAsync()
                .catch((error) => {
                Log.error('Error initializing StatsigClient', error);
            })
                .finally(() => {
                this._isLoadingSubject.next(false);
            });
        }
        else {
            Log.error('Must provide a client or sdkKey and user to initialize StatsigService');
            this._client = new StatsigClient('', {});
        }
        Log.debug('StatsigService created', this._client);
        this._checkAndEmitLoadingStatus();
        this._client.$on('values_updated', this._onValuesUpdated);
        SDKType._setBindingType('angular');
    }
    ngOnDestroy() {
        this._client.shutdown().catch((error) => {
            Log.error('An error occured during shutdown', error);
        });
        this._client.off('values_updated', this._onValuesUpdated);
    }
    checkGate(gateName, options) {
        Log.debug(`checkGate caled for ${gateName}, rv: ${this._renderVersion}`);
        return this._checkGateImpl(this._renderVersion, gateName, options);
    }
    getDynamicConfig(configName, options) {
        Log.debug(`getDynamicConfig called for ${configName}, rv: ${this._renderVersion}`);
        return this._getDynamicConfigImpl(this._renderVersion, configName, options);
    }
    getExperiment(experimentName, options) {
        Log.debug(`getExperiment caled for ${experimentName}, rv: ${this._renderVersion}`);
        return this._getExperimentImpl(this._renderVersion, experimentName, options);
    }
    getLayer(layerName, options) {
        Log.debug(`getLayer caled for ${layerName}, rv: ${this._renderVersion}`);
        return this._getLayerImpl(this._renderVersion, layerName, options);
    }
    logEvent(eventName, value, metadata) {
        this._client.logEvent(eventName, value, metadata);
    }
    updateUserAsync(user, options) {
        return this._client.updateUserAsync(user, options);
    }
    updateUserSync(user, options) {
        this._client.updateUserSync(user, options);
    }
    getClient() {
        return this._client;
    }
    _checkGateImpl(_rv, gateName, options) {
        return this._client.checkGate(gateName, options);
    }
    _getDynamicConfigImpl(_rv, configName, options) {
        return this._client.getDynamicConfig(configName, options);
    }
    _getExperimentImpl(_rv, experimentName, options) {
        return this._client.getExperiment(experimentName, options);
    }
    _getLayerImpl(_rv, layerName, options) {
        return this._client.getLayer(layerName, options);
    }
    _checkAndEmitLoadingStatus() {
        const isReady = _isReady(this._client);
        if (isReady) {
            this._isLoadingSubject.next(false);
            Log.debug('Client is now ready, loading status set to false');
        }
        else {
            this._isLoadingSubject.next(true);
            Log.debug('Client is not ready, loading status set to true');
        }
    }
    _onValuesUpdated = () => {
        this._renderVersion++;
        Log.debug('Values updated', this._renderVersion);
        this._checkAndEmitLoadingStatus();
    };
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: StatsigService, deps: [{ token: STATSIG_INIT_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: StatsigService, providedIn: 'root' });
}
__decorate([
    Memoize((...args) => {
        const [rv, gn, opt] = args;
        return `${rv}-${gn}-${JSON.stringify(opt)}`;
    }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, String, Object]),
    __metadata("design:returntype", Boolean)
], StatsigService.prototype, "_checkGateImpl", null);
__decorate([
    Memoize((...args) => {
        const [rv, gn, opt] = args;
        return `${rv}-${gn}-${JSON.stringify(opt)}`;
    }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, String, Object]),
    __metadata("design:returntype", Object)
], StatsigService.prototype, "_getDynamicConfigImpl", null);
__decorate([
    Memoize((...args) => {
        const [rv, en, opt] = args;
        return `${rv}-${en}-${JSON.stringify(opt)}`;
    }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, String, Object]),
    __metadata("design:returntype", Object)
], StatsigService.prototype, "_getExperimentImpl", null);
__decorate([
    Memoize((...args) => {
        const [rv, ln, opt] = args;
        return `${rv}-${ln}-${JSON.stringify(opt)}`;
    }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number, String, Object]),
    __metadata("design:returntype", Object)
], StatsigService.prototype, "_getLayerImpl", null);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: StatsigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [STATSIG_INIT_CONFIG]
                }] }], propDecorators: { _checkGateImpl: [], _getDynamicConfigImpl: [], _getExperimentImpl: [], _getLayerImpl: [] } });
function _isReady(client) {
    return client.loadingStatus === 'Ready';
}

class CheckGateDirective {
    _templateRef;
    _viewContainer;
    _statsigService;
    _gate = '';
    _isHidden = true;
    _options = {};
    set stgCheckGate(val) {
        Log.debug('Setting gate', val);
        if (val) {
            this._gate = val;
            this._updateView();
        }
    }
    set stgCheckGateOptions(val) {
        Log.debug('Setting options', val);
        if (val) {
            this._options = val;
            this._updateView();
        }
    }
    constructor(_templateRef, _viewContainer, _statsigService) {
        this._templateRef = _templateRef;
        this._viewContainer = _viewContainer;
        this._statsigService = _statsigService;
        _statsigService.getClient().$on('values_updated', () => {
            this._updateView();
        });
    }
    ngOnInit() {
        if (this._gate) {
            this._updateView();
        }
    }
    _updateView() {
        if (this._checkGate()) {
            if (this._isHidden) {
                Log.debug('Feature flag is enabled, showing the template');
                this._viewContainer.createEmbeddedView(this._templateRef);
                this._isHidden = false;
            }
        }
        else {
            this._viewContainer.clear();
            this._isHidden = true;
        }
    }
    _checkGate() {
        return (this._gate && this._statsigService.checkGate(this._gate, this._options));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CheckGateDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: StatsigService }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.1.3", type: CheckGateDirective, selector: "[stgCheckGate]", inputs: { stgCheckGate: "stgCheckGate", stgCheckGateOptions: "stgCheckGateOptions" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CheckGateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[stgCheckGate]',
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: StatsigService }], propDecorators: { stgCheckGate: [{
                type: Input
            }], stgCheckGateOptions: [{
                type: Input
            }] } });

const STATSIG_INIT_CONFIG = new InjectionToken('StatsigProvider');
class StatsigModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: StatsigModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: StatsigModule, declarations: [CheckGateDirective], exports: [CheckGateDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: StatsigModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: StatsigModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CheckGateDirective],
                    exports: [CheckGateDirective],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CheckGateDirective, STATSIG_INIT_CONFIG, StatsigModule, StatsigService };
